// store/slices/filtercriteriaReducer.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface FilterCriteriaReducer {
  field_to_filter: string;
  filter_value?: any[];
  name: string;
  min_exp?: number;
  max_exp?: number;
  start_year?: number;
  end_year?: number;
}

interface SearchState {
  industryData: any[];
  allCompanies: any[];
  industryLeaderData: any[];
  jobTitlesData: any[];
  locationsData: any[];
  excludeLocationsData: any[];
  universitiesData: any[];
  education_fields: any[];
  keywordsData: any[];
  skillsData: any[];
  minExp: number;
  maxExp: number;
  startYear: number;
  endYear: number;
  jobId: any;
  searchTitle: any;
  pipeLineId: any;
  isSavedSearch: boolean;
  db: boolean;
  manual_import: boolean;
  extension_import: boolean;
  ats: boolean;
  isFilterChange: boolean;
  isLoadingCandidates: boolean;
  filterQueries: any;
  search_only_lever_ss: boolean;
  search_only_greenhouse_mckinney: boolean;
}

const initialState: SearchState = {
  industryData: [],
  allCompanies: [],
  industryLeaderData: [],
  jobTitlesData: [],
  locationsData: [],
  excludeLocationsData: [],
  universitiesData: [],
  education_fields: [],
  keywordsData: [],
  skillsData: [],
  minExp: 0,
  maxExp: 60,
  startYear: 1960,
  endYear: 2030,
  jobId: null,
  searchTitle: null,
  pipeLineId: null,
  isSavedSearch: false,
  db: false,
  manual_import: false,
  extension_import: false,
  ats: false,
  isFilterChange: false,
  isLoadingCandidates: false,
  filterQueries: [],
  search_only_lever_ss: false,
  search_only_greenhouse_mckinney: false,
};

const filtercriteriaReducer = createSlice({
  name: "filtercriteria",
  initialState,
  reducers: {
    setIndustryData: (state, action: PayloadAction<any[]>) => {
      state.industryData = action.payload;
    },
    removeIndustryDataItem: (state, action: PayloadAction<any>) => {
      state.industryData = state.industryData.filter(item => item !== action.payload);
    },
    setAllCompaniesDataItem: (state, action: PayloadAction<any[]>) => {
      state.allCompanies = action.payload;
    },
    removeAllCompanyDataItem: (state, action: PayloadAction<any>) => {
      state.allCompanies = state.allCompanies.filter(item => item !== action.payload);
    },
    setIndustryLeaderData: (state, action: PayloadAction<any[]>) => {
      state.industryLeaderData = action.payload;
    },
    removeIndustryLeaderDataItem: (state, action: PayloadAction<any>) => {
      state.industryLeaderData = state.industryLeaderData.filter(item => item !== action.payload);
    },
    setJobTitlesData: (state, action: PayloadAction<any[]>) => {
      state.jobTitlesData = action.payload;
    },
    removeJobTitlesDataItem: (state, action: PayloadAction<any>) => {
      state.jobTitlesData = state.jobTitlesData.filter(item => item !== action.payload);
    },
    setLocationsData: (state, action: PayloadAction<any[]>) => {
      state.locationsData = action.payload;
    },
    removeLocationsDataItem: (state, action: PayloadAction<any>) => {
      state.locationsData = state.locationsData.filter(item => item !== action.payload);
    },
    setExcludeLocationsData: (state, action: PayloadAction<any[]>) => {
      state.excludeLocationsData = action.payload;
    },
    removeExcludeLocationsDataItem: (state, action: PayloadAction<any>) => {
      state.excludeLocationsData = state.excludeLocationsData.filter(item => item !== action.payload);
    },
    setUniversitiesData: (state, action: PayloadAction<any[]>) => {
      state.universitiesData = action.payload;
    },
    removeUniversitiesDataItem: (state, action: PayloadAction<any>) => {
      state.universitiesData = state.universitiesData.filter(item => item !== action.payload);
    },
    addKeywords: (state, action: PayloadAction<any>) => {
      const parent = state.keywordsData.filter((ind: any) => ind !== action.payload);
      state.keywordsData = [...parent, action.payload];
    },
    setKeywordsData: (state, action: PayloadAction<any[]>) => {
      state.keywordsData = action.payload;
    },
    removeKeywordsDataItem: (state, action: PayloadAction<any>) => {
      state.keywordsData = state.keywordsData.filter(item => item !== action.payload);
    },
    setSkillsData: (state, action: PayloadAction<any[]>) => {
      state.skillsData = action.payload;
    },
    removeSkillsDataItem: (state, action: PayloadAction<any>) => {
      state.skillsData = state.skillsData.filter(item => item !== action.payload);
    },
    setMinExp: (state, action: PayloadAction<number>) => {
      state.minExp = action.payload;
    },
    setMaxExp: (state, action: PayloadAction<number>) => {
      state.maxExp = action.payload;
    },
    addDegreeData: (state, action: PayloadAction<any>) => {
      state.education_fields = action.payload;
    },
    removeDegreeData: (state, action: PayloadAction<any>) => {
      state.education_fields = state.education_fields.filter(item => item !== action.payload);
    },
    setStartYear: (state, action: PayloadAction<number>) => {
      state.startYear = action.payload;
    },
    setEndYear: (state, action: PayloadAction<number>) => {
      state.endYear = action.payload;
    },
    setJobId: (state, action: PayloadAction<any>) => {
      state.jobId = action.payload;
    },
    setSearchTitle: (state, action: PayloadAction<any>) => {
      state.searchTitle = action.payload;
    },
    setPipeLineId: (state, action: PayloadAction<any>) => {
      state.pipeLineId = action.payload;
    },
    setIsSavedSearch: (state, action: PayloadAction<boolean>) => {
      state.isSavedSearch = action.payload;
    },
    setIsDB: (state, action: PayloadAction<boolean>) => {
      state.db = action.payload;
    },
    setManualImport: (state, action: PayloadAction<boolean>) => {
      state.manual_import = action.payload;
    },
    setExtensionImport: (state, action: PayloadAction<boolean>) => {
      state.extension_import = action.payload;
    },
    setATS: (state, action: PayloadAction<boolean>) => {
      state.ats = action.payload;
    },
    setIsFilterChange: (state, action: PayloadAction<boolean>) => {
      state.isFilterChange = action.payload;
    },
    setIsLoadingCandidates: (state, action: PayloadAction<boolean>) => {
      state.isLoadingCandidates = action.payload;
    },
    setIsLever: (state, action: PayloadAction<boolean>) => {
      state.search_only_lever_ss = action.payload;
    },
   
    setIsGreenHouse: (state, action: PayloadAction<boolean>) => {
      state.search_only_greenhouse_mckinney = action.payload;
    },
    addSearchQuery: (state, action: PayloadAction<any>) => {
      const parent = state.filterQueries.filter((ind: any) => ind !== action.payload);
      state.filterQueries = [...parent, action.payload];
    },
    setAllFilterCriteria: (state, action: PayloadAction<SearchState>) => {
      Object.assign(state, action.payload); // Update the entire state
    },

    // Function to reset all values
    resetFilterCriteria: (state) => {
      Object.assign(state, initialState); // Reset state to initial values
    },
  },
});

export const {
  setIndustryData,
  removeAllCompanyDataItem,
  setAllCompaniesDataItem,
  removeIndustryDataItem,
  setIndustryLeaderData,
  removeIndustryLeaderDataItem,
  setJobTitlesData,
  removeJobTitlesDataItem,
  setLocationsData,
  removeLocationsDataItem,
  setExcludeLocationsData,
  removeExcludeLocationsDataItem,
  setUniversitiesData,
  removeUniversitiesDataItem,
  addKeywords,
  setKeywordsData,
  removeKeywordsDataItem,
  setSkillsData,
  removeSkillsDataItem,
  setMinExp,
  setMaxExp,
  setStartYear,
  setEndYear,
  addDegreeData,
  removeDegreeData,
  setJobId,
  setSearchTitle,
  setPipeLineId,
  setIsSavedSearch,
  setIsDB,
  setManualImport,
  setExtensionImport,
  setATS,
  setIsFilterChange,
  setIsLoadingCandidates,
  addSearchQuery,
  setIsGreenHouse,
  setIsLever,
  setAllFilterCriteria,
  resetFilterCriteria
} = filtercriteriaReducer.actions;

export default filtercriteriaReducer.reducer;
